import React from "react";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";

import "@fontsource/dm-sans"; // Defaults to weight 400
import "@fontsource/dm-sans/400.css"; // Specify weight
import "@fontsource/dm-sans/400-italic.css"; // Specify weight and style

import "./index.css";
import "aha-components/theme.css";
import App from "./App";
import { ClerkProvider } from "@clerk/clerk-react";
import { datafluxRum } from "@cloudcare/browser-rum";
import appConfig from "./utils/config";
import { setupGlobalErrorHandler, shouldFilterError } from "./utils/globalErrorHandler";
import ModalAddPayment from "@/components/modal/ModalAddPayment";
import ModalPaymentBoundCard from "@/components/modal/ModalPaymentBoundCard";
import ModalUpdatePlan from "@/components/modal/ModalUpdatePlan";
import ModalRechargeResult from "@/components/modal/ModalRechargeResult";
import { BrowserRouter } from "react-router-dom";
import { isMobile } from "./biz/tool";
import { useModalStore } from "./hooks/useModalStore";
import { CreateOrganization, OrganizationProfile } from "@clerk/clerk-react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import WaitListShareModal from "@/pages/headChatBot/components/WaitListShareModal";
import ChargeModal from "@/components/ChargeModal";
import ChangeBudgetModal from "@/components/ChangeBudgetModal";
import MatchInfluencerAdjustModal from "@/components/MatchInfluencerAdjustModal";
import AffiliateNoRechargeModal from "./components/modal/AffiliateNoRechargeModal";
import CheckDeposit from "@/components/modal/CheckDepositModal";
import PublishedVideoModal from "./components/PublishedVideoModal";
import RateModal from "./components/modal/RateModal";
// 导入Clerk官方本地化配置
import { zhCN, enUS } from "@clerk/localizations";
import InfluencerTrackLinkModal from "./components/modal/InfluencerTrackLinkModal";
import KeepOrRemoveCreatorModal from "./components/KeepOrRemoveCreatorModal";
import { getInitialLanguage } from "./i18n";
import { isFeishuEnv, preloadFeishuJsapiInBackground } from "./utils/feishu";
import getEnv from "./utils/getEnv";
import "aha-components/dist/index.css";

const PUBLISHABLE_KEY = appConfig["clerkPublishableKey"];

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk publishable key to the .env.local file");
}

// 获取Clerk本地化配置，使用官方包
const getClerkLocalization = () => {
  const currentLang = getInitialLanguage();

  if (currentLang === "zh-CN") {
    return zhCN as any;
  }

  // 英语配置
  return enUS as any;
};

// if (process.env.REACT_APP_ENV !== 'development') {
// 使用 guance 云面板
// 工作空间 test/prod -> 用户访问监测 -> 应用 advertiser-platform -> 分析看板
// 地址：
// https://us1-console.guance.com/rum/list

// 初始化全局错误处理
setupGlobalErrorHandler();

const initializeNonCriticalSdks = () => {
  datafluxRum.init({
    applicationId: appConfig["applicationId"],
    clientToken: appConfig["clientToken"],
    env: appConfig["env"],
    allowedTracingUrls: [appConfig["BASE_URL"]],
    version: "1.0.3",
    service: "browser",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 30, // 降低采样率以减少事件队列大小
    trackUserInteractions: true,
    site: "https://rum-openway.guance.one",
    // 添加 beforeSend 回调来过滤已知的无害错误
    // beforeSend 接收两个参数：event 和 domainContext（domainContext 可选）
    beforeSend: (event: any, domainContext?: any) => {
      // 只处理错误类型的事件，其他类型的事件（action, view, resource 等）正常发送
      if (event?.type === "error") {
        try {
          // 检查 event.error 对象（主要错误对象）
          if (event.error && shouldFilterError(event.error)) {
            return false; // 返回 false 表示不发送此事件
          }
          // 检查 event.message（某些情况下错误信息在这里）
          if (
            event.message &&
            typeof event.message === "string" &&
            shouldFilterError(event.message)
          ) {
            return false;
          }
          // 检查 event.stack（堆栈信息）
          if (
            event.stack &&
            typeof event.stack === "string" &&
            shouldFilterError(event.stack)
          ) {
            return false;
          }
          // 检查 event.error.message
          if (
            event.error?.message &&
            typeof event.error.message === "string" &&
            shouldFilterError(event.error.message)
          ) {
            return false;
          }
          // 检查 event.error.stack
          if (
            event.error?.stack &&
            typeof event.error.stack === "string" &&
            shouldFilterError(event.error.stack)
          ) {
            return false;
          }
        } catch (e) {
          // 如果过滤逻辑出错，为了安全起见，允许错误上报
          console.warn("Error in beforeSend filter:", e);
          return true;
        }
      }
      // 其他事件（非错误类型，或错误但不在过滤列表中）正常发送
      return true;
    },
  });
  datafluxRum.startSessionReplayRecording();

  ReactGA.initialize([
    {
      trackingId: "G-X2F66DVGNZ",
    },
  ]);
};

const idleCallback =
  (window as any).requestIdleCallback ||
  ((cb: Function) => window.setTimeout(() => cb(), 1200));
idleCallback(() => {
  initializeNonCriticalSdks();
});

const isTestEnv = getEnv() !== "production";

if (isFeishuEnv()) {
  preloadFeishuJsapiInBackground();
}

if (isFeishuEnv() && isTestEnv) {
  import("vconsole")
    .then(({ default: VConsole }) => {
      if (!(window as any).__AHA_VCONSOLE__) {
        (window as any).__AHA_VCONSOLE__ = new VConsole();
        const style = document.createElement("style");
        style.id = "aha-vconsole-style";
        style.textContent = "#__vconsole .vc-switch{bottom:100px !important;}";
        document.head.appendChild(style);
        console.info("[AhaDebug] vConsole enabled in Feishu environment");
      }
    })
    .catch((error) => {
      console.error("[AhaDebug] Failed to initialize vConsole:", error);
    });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const ModalBox = () => {
  const { showCreateOrg, showManageOrg, setShowCreateOrg, setShowManageOrg } =
    useModalStore();

  return (
    <>
      <ModalAddPayment />
      <ModalPaymentBoundCard />
      <ModalUpdatePlan />
      <ModalRechargeResult />
      <WaitListShareModal />
      {/* 充值弹窗 */}
      <ChargeModal />
      {/* 修改 influences budget modal */}
      <ChangeBudgetModal />
      {/* 组织管理弹窗 */}
      <Modal
        open={showManageOrg}
        onCancel={() => setShowManageOrg(false)}
        footer={null}
        classNames={{
          content: "!p-0",
        }}
        width={880}
        closeIcon={<CloseOutlined className="text-gray-500 text-lg" />}
        centered
        styles={{
          mask: {
            background: "rgba(0, 0, 0, 0.5)",
          },
        }}
        className="clerk-modal"
      >
        <OrganizationProfile />
      </Modal>
      <Modal
        open={showCreateOrg}
        onCancel={() => setShowCreateOrg(false)}
        footer={null}
        classNames={{
          content: "!p-0",
        }}
        width={432}
        closeIcon={<CloseOutlined className="text-gray-500 text-lg" />}
        centered
        styles={{
          mask: {
            background: "rgba(0, 0, 0, 0.5)",
          },
        }}
        className="clerk-modal"
      >
        <CreateOrganization />
      </Modal>
      {/* 广告主调整达人匹配结果的提交弹窗 */}
      <MatchInfluencerAdjustModal />
      {/* Affiliate 不充值弹窗 */}
      <AffiliateNoRechargeModal />
      {/* 余额不够 提示充值的弹窗 */}
      <CheckDeposit />
      {/* 发布后需要做什么的介绍视频弹窗 */}
      <PublishedVideoModal />
      {/* 达人评分弹窗 */}
      <RateModal />
      {/* 填写达人专属track链接弹窗 */}
      <InfluencerTrackLinkModal />
      {/* campaign 修改过后已经报价过的达人去留的选择弹窗 */}
      <KeepOrRemoveCreatorModal />
    </>
  );
};

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl={`${isMobile() ? "/mLogin" : "/"}`}
        localization={{
          ...getClerkLocalization(),
          socialButtonsBlockButton: 'Continue with Google (work email preferred)',
          organizationProfile: {
            start: {
              profileSection: {
                uploadAction__title: "Logo(optional)",
              },
            },
          },
          signUp: {
            start: {
              title: "Welcome to AhaCreator",
              subtitle: "",
            },
          },
          unstable__errors: {
            not_allowed_access: "Please use a email ",
            form_identifier_exists:
              "You already have an account. Log in instead.",
          },
        }}
        appearance={{
          variables: {
            colorPrimary: "#FB6011",
          },
        }}
      >
        <ModalBox />
        <App />
      </ClerkProvider>
    </BrowserRouter>
  </React.StrictMode>
);
